/**
 * `/inventory/transfers` — the printed-transfer (film) inventory tab (printed-transfer-inventory Phase 0).
 *
 * A read-only table of physical, already-printed DTF transfers on hand, keyed by design + size band,
 * plus a physical COUNT / ADJUST recorder. On-hand is the derived running sum of the ledger, not a
 * hand-kept counter. Shelf stock recorded here IS on the build path: the gang-sheet build nets these
 * films out (#161–#166), so new sheets skip designs already covered by the shelf. A record RESOLVES
 * the design server-side first (unknown design 404s, a band with no READY print 422s), so the ledger
 * can never hold a phantom identity.
 *
 * DTF only: screen-print films have no stable design identity to key on and are out of scope.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PackagePlus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { invalidateInventory } from './inventory-lib';
import { MovementList } from './movements-dialog';

type TransferOnHand = components['schemas']['TransferOnHand'];
type DesignSummary = components['schemas']['DesignSummary'];
type SizeBand = 'adult' | 'youth';

/** Print size like `12 × 16 in`, trimming a trailing `.0`. */
function sizeLabel(w: number | undefined, h: number | undefined): string {
  if (w == null || h == null) return '—';
  const fmt = (n: number) => `${Number(n.toFixed(1))}`;
  return `${fmt(w)} × ${fmt(h)} in`;
}

const bandLabel: Record<SizeBand, string> = { adult: 'Adult', youth: 'Youth' };

type Prefill = { designId: string; band: SizeBand; designName?: string };

export function TransferInventoryPage() {
  const client = useApiClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [recordOpen, setRecordOpen] = useState(false);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [history, setHistory] = useState<Prefill | null>(null);

  const transfers = useQuery({
    queryKey: ['transfer-inventory'],
    queryFn: async () => {
      const { data, error } = await client.GET('/print-inventory');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load printed transfers.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });

  const items = transfers.data ?? [];

  const openBlank = () => {
    setPrefill(null);
    setRecordOpen(true);
  };
  const openFor = (t: TransferOnHand) => {
    setPrefill({ designId: t.designId, band: t.sizeBand as SizeBand, designName: t.designName });
    setRecordOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Already-printed DTF transfers (films) on the shelf, ready to press — by design and size.
          New gang sheets skip prints already covered here.
        </p>
        <Button
          size="sm"
          onClick={openBlank}
          disabled={!canWrite}
          title={canWrite ? undefined : 'Admins only'}
        >
          <PackagePlus className="size-4" />
          Record transfer
        </Button>
      </div>

      {transfers.isPending ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading printed transfers…
        </div>
      ) : transfers.isError ? (
        <p className="py-8 text-sm text-destructive">
          {transfers.error instanceof Error ? transfers.error.message : 'Failed to load.'}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No printed transfers recorded yet. Use{' '}
          <span className="font-medium">Record transfer</span> to count the transfers you already
          have on the shelf.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Design</th>
                <th className="px-3 py-2 text-left font-medium">Size</th>
                <th className="px-3 py-2 text-left font-medium">Print size</th>
                <th className="px-3 py-2 text-right font-medium">On hand</th>
                <th className="px-3 py-2 text-right font-medium">Last count</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={`${t.designId}#${t.sizeBand}`} className="border-t">
                  <td className="px-3 py-2 font-medium">{t.designName ?? t.designId}</td>
                  <td className="px-3 py-2">{bandLabel[t.sizeBand as SizeBand]}</td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {sizeLabel(t.widthIn ?? undefined, t.heightIn ?? undefined)}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      t.onHand < 0 && 'font-semibold text-red-700 dark:text-red-400',
                    )}
                  >
                    {t.onHand}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {t.lastCountedQty ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mr-1 text-muted-foreground"
                      onClick={() =>
                        setHistory({
                          designId: t.designId,
                          band: t.sizeBand as SizeBand,
                          designName: t.designName,
                        })
                      }
                    >
                      History
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openFor(t)}
                      disabled={!canWrite}
                    >
                      Update
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordTransferDialog
        open={recordOpen}
        prefill={prefill}
        existing={items}
        onClose={() => setRecordOpen(false)}
      />

      <Dialog open={history !== null} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-lg">
          {history && (
            <TransferHistoryBody key={`${history.designId}#${history.band}`} target={history} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** The shelf transfers' ledger history (production-records R0): counts, adjusts, and the PULLs that
 *  served orders — each pull links back to its order. */
function TransferHistoryBody({ target }: { target: Prefill }) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['transfer-inventory', 'detail', target.designId, target.band],
    queryFn: async () => {
      const { data, error } = await client.GET('/print-inventory/{designId}/{band}', {
        params: { path: { designId: target.designId, band: target.band } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the transfer history.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          History — {target.designName ?? target.designId} · {bandLabel[target.band]}
        </DialogTitle>
      </DialogHeader>
      {query.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      ) : (
        <MovementList
          rows={query.data.movements}
          emptyText="No history yet — record a count to start one."
        />
      )}
    </>
  );
}

function RecordTransferDialog({
  open,
  prefill,
  existing,
  onClose,
}: {
  open: boolean;
  prefill: Prefill | null;
  existing: TransferOnHand[];
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] sm:max-w-lg">
        {open && <RecordTransferBody prefill={prefill} existing={existing} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function RecordTransferBody({
  prefill,
  existing,
  onClose,
}: {
  prefill: Prefill | null;
  existing: TransferOnHand[];
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [design, setDesign] = useState<DesignSummary | null>(null);
  const [band, setBand] = useState<SizeBand>(prefill?.band ?? 'adult');
  // 'count' anchors on-hand to a physical count; 'adjust' applies a signed relative correction.
  const [mode, setMode] = useState<'count' | 'adjust'>('count');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  // One idempotency token per submit context, reused across a failed-then-retry so the server dedupes.
  const tokenRef = useRef<string | null>(null);
  const resetToken = () => {
    tokenRef.current = null;
  };

  const designId = prefill?.designId ?? design?.designId ?? '';
  const designName = prefill?.designName ?? design?.name ?? designId;

  // Design picker (only when not pre-filled from a row). Designs are name-sorted + small at this scale.
  const designsQuery = useQuery({
    queryKey: ['designs'],
    enabled: !prefill,
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllDesigns(client),
  });
  const [filter, setFilter] = useState('');
  const designs = useMemo(() => {
    const all = designsQuery.data ?? [];
    const f = filter.trim().toLowerCase();
    return f ? all.filter((d) => d.name.toLowerCase().includes(f)) : all;
  }, [designsQuery.data, filter]);

  // Which bands have a READY print (only those can be inventoried). When pre-filled, the band came from
  // the ledger (already a valid, inventoried transfer), so it is trusted.
  const readyBands: Record<SizeBand, boolean> = prefill
    ? { adult: true, youth: true }
    : {
        adult: design?.adult?.status === 'READY',
        youth: design?.youth?.status === 'READY',
      };
  // A band is selectable when it has a READY print; in prefill (Update) mode the band is LOCKED to the
  // row's band — switching would turn it into a different, possibly non-READY transfer we can't validate
  // client-side (to inventory another band, use Record transfer + the picker).
  const bandDisabled = (b: SizeBand): boolean => (prefill ? b !== band : !readyBands[b]);
  // Gate Save on the chosen band being ready too — otherwise the client offers to submit a band the
  // server can only 422 (e.g. a design still validating). Prefill's band is trusted (see above).
  const bandReady = prefill ? true : readyBands[band];

  const currentOnHand =
    existing.find((t) => t.designId === designId && t.sizeBand === band)?.onHand ?? 0;
  const n = Number(qty.trim());
  const validQty =
    qty.trim() !== '' && Number.isInteger(n) && (mode === 'count' ? n >= 0 : n !== 0);
  const nextOnHand = !validQty ? null : mode === 'count' ? n : currentOnHand + n;

  const record = useMutation({
    mutationFn: async () => {
      if (!designId) throw new Error('Pick a design first.');
      if (!tokenRef.current) tokenRef.current = crypto.randomUUID();
      const token = tokenRef.current;
      if (mode === 'count') {
        const { data, error } = await client.POST('/print-inventory/{designId}/{band}/count', {
          params: { path: { designId, band } },
          body: {
            countedQty: Math.trunc(n),
            reason: reason.trim() || undefined,
            clientToken: token,
          },
        });
        if (error || !data?.ok)
          throw new Error(error?.error?.detail ?? 'Could not record the count.');
        return data.data;
      }
      const { data, error } = await client.POST('/print-inventory/{designId}/{band}/adjust', {
        params: { path: { designId, band } },
        body: { qtyDelta: Math.trunc(n), reason: reason.trim() || undefined, clientToken: token },
      });
      if (error || !data?.ok)
        throw new Error(error?.error?.detail ?? 'Could not record the adjustment.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      resetToken();
      setQty('');
      setReason('');
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{prefill ? `Update ${designName}` : 'Record a printed transfer'}</DialogTitle>
        <DialogDescription>
          <span className="font-medium">Count</span> sets on hand to what you counted on the shelf.{' '}
          <span className="font-medium">Adjust</span> adds or removes a few (scrap, damage, found).
        </DialogDescription>
      </DialogHeader>

      {!prefill && (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">Design</Label>
          {design ? (
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="font-medium">{design.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDesign(null);
                  record.reset();
                  resetToken();
                }}
              >
                Change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="Search designs by name…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {designsQuery.isPending ? (
                  <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" /> Loading designs…
                  </div>
                ) : designs.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">No designs match.</p>
                ) : (
                  designs.map((d) => (
                    <button
                      type="button"
                      key={d.designId}
                      onClick={() => {
                        setDesign(d);
                        // Land on a band that actually has a READY print.
                        setBand(d.adult?.status === 'READY' ? 'adult' : 'youth');
                        record.reset();
                        resetToken();
                      }}
                      className="flex w-full items-center justify-between border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                    >
                      <span className="font-medium">{d.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {d.adult?.status === 'READY' ? 'adult' : ''}
                        {d.youth?.status === 'READY' ? ' youth' : ''}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {(prefill || design) && (
        <div className="space-y-3 rounded-md border p-3">
          {/* Band toggle — only bands with a READY print are selectable; locked to the row in Update mode. */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground">Size band</Label>
            <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
              {(['adult', 'youth'] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  disabled={bandDisabled(b)}
                  onClick={() => {
                    setBand(b);
                    record.reset();
                    resetToken();
                  }}
                  className={cn(
                    'flex-1 rounded px-2 py-1',
                    band === b ? 'bg-background shadow-sm' : 'text-muted-foreground',
                    bandDisabled(b) && 'cursor-not-allowed opacity-40',
                  )}
                  title={
                    bandDisabled(b)
                      ? prefill
                        ? 'This row is locked to its size — use Record transfer for the other size'
                        : 'No READY print for this band'
                      : undefined
                  }
                >
                  {bandLabel[b]}
                </button>
              ))}
            </div>
          </div>

          {/* Mode toggle — Count sets on-hand; Adjust applies a ± change. */}
          <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
            <button
              type="button"
              onClick={() => {
                setMode('count');
                record.reset();
                resetToken();
              }}
              className={
                mode === 'count'
                  ? 'flex-1 rounded bg-background px-2 py-1 shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-muted-foreground'
              }
            >
              Count (set)
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('adjust');
                record.reset();
                resetToken();
              }}
              className={
                mode === 'adjust'
                  ? 'flex-1 rounded bg-background px-2 py-1 shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-muted-foreground'
              }
            >
              Adjust (±)
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">On hand now</Label>
              <div className="tabular-nums">{currentOnHand}</div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="transfer-qty" className="text-xs text-muted-foreground">
                {mode === 'count' ? 'Set to' : 'Change by (±)'}
              </Label>
              <Input
                id="transfer-qty"
                type="number"
                inputMode="numeric"
                step="1"
                min={mode === 'count' ? '0' : undefined}
                className="h-8"
                placeholder={mode === 'count' ? undefined : '+ / -'}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">New on hand</Label>
              <div className="tabular-nums font-medium">{nextOnHand ?? '—'}</div>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="transfer-reason" className="text-xs text-muted-foreground">
              Reason (optional)
            </Label>
            <Input
              id="transfer-reason"
              placeholder={mode === 'count' ? 'physical count' : 'e.g. creased / misprint'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {designName} · {bandLabel[band]}
            </div>
            <Button
              onClick={() => record.mutate()}
              disabled={!validQty || !bandReady || record.isPending}
            >
              {record.isPending && <Loader2 className="size-4 animate-spin" />}
              {mode === 'count' ? 'Save count' : 'Save adjustment'}
            </Button>
          </div>

          {record.isError && (
            <p className="text-xs text-destructive">
              {record.error instanceof Error ? record.error.message : 'Save failed.'}
            </p>
          )}
          {record.isSuccess && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              Saved — on hand is now {record.data.onHand}.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );
}
